import axios from "axios";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError("");
  };

  const validatePassword = (password: string) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const passwordsMatch = formData.password === formData.confirmPassword;

  const canSubmit =
    formData.firstName &&
    formData.lastName &&
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    passwordsMatch &&
    validatePassword(formData.password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordsMatch) {
      return;
    }

    if (!validatePassword(formData.password)) {
      setFormError(
        "Le mot de passe doit contenir au moins 8 caractères, une majuscule et un chiffre."
      );
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${API_BASE_URL}/auth/register`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
      });
      toast.info("Inscription réussie !");
      navigate("/login", { replace: true });
    } catch (error: any) {
      console.error(
        "Erreur lors de l'inscription",
        error.response?.data || error.message
      );
      setFormError(
        error.response?.data?.message || "Erreur lors de l'inscription"
      );
    }
  };
  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6  lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-gray-900">
          Créer un compte
        </h2>
      </div>

      <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Prénom
            </label>
            <div className="mt-2">
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 border-2 border-solid border-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
                placeholder="Votre prénom"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Nom
            </label>
            <div className="mt-2">
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 border-2 border-solid border-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
                placeholder="Votre nom"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Adresse mail
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 border-2 border-solid border-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
                placeholder="nom@exemple.com"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Mot de passe
            </label>
            <div className="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className={`block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 border-2 ${
                  formData.password && !validatePassword(formData.password)
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm`}
                placeholder="••••••••"
              />
              {/* Petit message rouge si mot de passe faible */}
              {formData.password && !validatePassword(formData.password) && (
                <p className="mt-1 text-sm text-red-500">
                  Le mot de passe doit contenir au moins 8 caractères, une
                  majuscule et un chiffre.
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Confirmer le mot de passe
            </label>
            <div className="mt-2">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className={`block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 border-2 ${
                  !passwordsMatch && formData.confirmPassword
                    ? "border-red-500"
                    : "border-gray-300"
                } placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm`}
                placeholder="••••••••"
              />
              {!passwordsMatch && formData.confirmPassword && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                  Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm ${
                canSubmit
                  ? "bg-indigo-600 hover:bg-indigo-500"
                  : "bg-gray-400 cursor-not-allowed"
              } focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
            >
              Créer mon compte
            </button>
          </div>
        </form>

        <p className="mt-2 text-center text-sm text-gray-500">
          Vous avez déjà un compte ?{" "}
          <Link
            to="/login"
            className="font-semibold text-indigo-600 hover:text-indigo-500 "
          >
            Connectez-vous ici
          </Link>
        </p>
      </div>
    </div>
  );
}
